import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { X, Loader2, ArrowRight } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { getAttribution } from '@/lib/attribution';
import { Field, Honeypot, Consent, FormError } from '@/components/forms/fields';
import { trackEvent, EVENTS } from '@/lib/analytics';

/**
 * Enquiry form shown from the proof section.
 *
 * Posts to the existing /leads/contact pipeline rather than a new endpoint, so
 * nothing on the backend changes. That model has no business-name field, so the
 * business name is written into the message under a clear label instead of
 * being silently dropped.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function ProfileEnquiryModal({ open, onClose, source }) {
  const [data, setData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    description: '',
    consent: false,
    hp: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const close = useCallback(() => {
    onClose();
    const el = returnFocusRef.current;
    if (el && typeof el.focus === 'function') el.focus();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || !nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const id = window.setTimeout(() => {
      panelRef.current?.querySelector('input, textarea')?.focus();
    }, 60);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  const submit = async () => {
    const e = {};
    if (data.name.trim().length < 2) e.name = 'Enter your name.';
    if (data.business.trim().length < 2) e.business = 'Enter your business name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) e.email = 'Enter a valid email address.';
    if (data.phone.trim().length < 6) e.phone = 'Enter a phone number we can reach you on.';
    if (data.description.trim().length < 5) e.description = 'Tell us briefly what you need.';
    if (!data.consent) e.consent = 'Consent is required to submit.';
    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);
    setSubmitError(null);
    try {
      const res = await api.post('/leads/contact', {
        intent: 'contractor_growth',
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: `Business: ${data.business}\nEnquiry source: ${source}\n\n${data.description}`,
        consent: data.consent,
        hp: data.hp,
        attribution: getAttribution(),
      });
      trackEvent(EVENTS.profileEnquirySubmit, { source });
      onClose();
      navigate('/thank-you', { state: { referenceId: res?.data?.id } });
    } catch (err) {
      setSubmitError(errorMessage(err, 'We could not send your details. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center p-4 sm:items-center"
      data-testid="profile-enquiry-modal"
    >
      <div className="absolute inset-0 bg-ink/75" onClick={close} aria-hidden="true" />

      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-enquiry-title"
        aria-describedby="profile-enquiry-desc"
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reduce ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-md bg-bone px-6 py-8 sm:px-10 sm:py-10 shadow-[0_40px_100px_rgba(5,5,7,0.55)]"
      >
        <button
          onClick={close}
          aria-label="Close"
          data-testid="profile-enquiry-close"
          className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-md text-navy/60 transition-colors hover:bg-navy/5 hover:text-navy"
        >
          <X className="h-5 w-5" />
        </button>

        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel">{source}</p>
        <h2
          id="profile-enquiry-title"
          className="mt-4 pr-10 font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug text-navy"
        >
          For more information, send us your details.
        </h2>
        <p id="profile-enquiry-desc" className="mt-3 text-[15px] leading-relaxed text-navy/65">
          Tell us about your business and what you are trying to improve, and we will come back to you directly.
        </p>

        <form
          noValidate
          className="relative mt-8"
          data-testid="profile-enquiry-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            submit();
          }}
        >
          <Honeypot value={data.hp} onChange={(v) => setData((d) => ({ ...d, hp: v }))} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name" htmlFor="pe-name" error={errors.name}>
              <input
                id="pe-name"
                data-testid="pe-name"
                className="input-field"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                autoComplete="name"
              />
            </Field>
            <Field label="Business name" htmlFor="pe-business" error={errors.business}>
              <input
                id="pe-business"
                data-testid="pe-business"
                className="input-field"
                value={data.business}
                onChange={(e) => set('business', e.target.value)}
                autoComplete="organization"
              />
            </Field>
            <Field label="Email" htmlFor="pe-email" error={errors.email}>
              <input
                id="pe-email"
                data-testid="pe-email"
                type="email"
                className="input-field"
                value={data.email}
                onChange={(e) => set('email', e.target.value)}
                autoComplete="email"
              />
            </Field>
            <Field label="Phone" htmlFor="pe-phone" error={errors.phone}>
              <input
                id="pe-phone"
                data-testid="pe-phone"
                type="tel"
                className="input-field"
                value={data.phone}
                onChange={(e) => set('phone', e.target.value)}
                autoComplete="tel"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description" htmlFor="pe-description" error={errors.description}>
                <textarea
                  id="pe-description"
                  data-testid="pe-description"
                  rows={4}
                  className="input-field resize-none"
                  value={data.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="mt-6">
            <Consent
              checked={data.consent}
              onChange={(v) => set('consent', v)}
              error={errors.consent}
              testid="pe-consent"
            />
          </div>

          {submitError && <FormError message={submitError} onRetry={submit} />}

          <button
            type="submit"
            disabled={loading}
            data-testid="pe-submit"
            className="btn-primary group mt-8 min-w-[12rem] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit
            {!loading && (
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
